import { useCallback } from 'react';
import {
  faBan,
  faCheck,
  faChevronDown,
  faWarning,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  AssistChip,
  IndeterminateCircularProgressIndicator,
  Menu,
  MenuItem,
  Stack,
} from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiGetUserAssignedBadgeResponseBody } from '~/lib/api/modules/Users';
import { useUpdateAssignedUserBadgeMutation } from '~/lib/api/modules/UserBadges/hooks/useUpdateAssignedUserBadgeMutation';

export type ISearchCommunityListItemAssignedBadgeProps = IContainerProps & {
  assignedBadge: IApiGetUserAssignedBadgeResponseBody;
  appId: string;
  userId: string;
  label: string;
  onUpdate?: () => void;
};

export const SearchCommunityListItemAssignedBadge: React.FC<
  ISearchCommunityListItemAssignedBadgeProps
> = (props) => {
  const { assignedBadge, label, appId, userId, onUpdate } = props;
  const { t } = useTranslation();
  const updateAssignedUserBadgeMutation = useUpdateAssignedUserBadgeMutation();

  const handleUpdateUserBadgeRequest = useCallback(
    (
      userBadgeId: string,
      action: 'validate' | 'remove' | 'reject',
    ): Promise<unknown> | void => {
      if (updateAssignedUserBadgeMutation.isLoading) {
        return;
      }

      return updateAssignedUserBadgeMutation
        .mutateAsync({
          appId,
          userBadgeId,
          body: {
            action,
            userId,
          },
        })
        .then(() => {
          onUpdate?.();
        });
    },
    [updateAssignedUserBadgeMutation, appId, userId, onUpdate],
  );

  return (
    <Menu
      trigger={({ getProps }) => (
        <AssistChip
          icon={
            updateAssignedUserBadgeMutation.isLoading ? (
              <IndeterminateCircularProgressIndicator />
            ) : assignedBadge.status === 'requested' ? (
              <FontAwesomeIcon icon={faWarning} />
            ) : assignedBadge.status === 'rejected' ? (
              <FontAwesomeIcon icon={faXmark} />
            ) : undefined
          }
          label={
            <Stack horizontal gap={2}>
              <div>{label}</div>
              <FontAwesomeIcon icon={faChevronDown} />
            </Stack>
          }
          disabled={updateAssignedUserBadgeMutation.isLoading}
          {...getProps()}
        />
      )}
      placement="bottom-start"
    >
      {assignedBadge.status === 'requested' && (
        <>
          <MenuItem
            label={t('SearchCommunityListItemAssignedBadge.validate.label')}
            leadingIcon={<FontAwesomeIcon icon={faCheck} />}
            onClick={() =>
              handleUpdateUserBadgeRequest(assignedBadge.id, 'validate')
            }
          />
          <MenuItem
            label={t('SearchCommunityListItemAssignedBadge.reject.label')}
            supportingText={t(
              'SearchCommunityListItemAssignedBadge.reject.supportingText',
            )}
            leadingIcon={<FontAwesomeIcon icon={faBan} />}
            onClick={() =>
              handleUpdateUserBadgeRequest(assignedBadge.id, 'reject')
            }
          />
        </>
      )}
      <MenuItem
        label={t('SearchCommunityListItemAssignedBadge.remove.label')}
        supportingText={t(
          'SearchCommunityListItemAssignedBadge.remove.supportingText',
        )}
        leadingIcon={<FontAwesomeIcon icon={faXmark} />}
        onClick={() => handleUpdateUserBadgeRequest(assignedBadge.id, 'remove')}
      />
    </Menu>
  );
};
